/**
 * Typed client for settings.yaml v1.0.0 — the Platform Config API (M6-07).
 *
 *  - GET /admin/settings + GET/PUT /admin/settings/{key}   — settings catalog
 *    (`settings.setting.view` / `.manage`; values only, §14.4 forms rule).
 *  - GET /admin/flags + PUT /admin/flags/{key}              — flag catalog +
 *    system-wide toggle (`settings.flag.view` / `.manage`).
 *  - PUT/DELETE /admin/flags/{key}/overrides/{merchantId}   — per-merchant
 *    overrides (MERCHANT-scoped flags only).
 *  - GET /config/flags                                      — the caller's
 *    EFFECTIVE flag map (no permission required), which is what the panel
 *    gates its own surfaces on (M7-96).
 */

import type { ApiClient } from "./client";
import { queryString, type PageQuery, type Paginated, type Ulid } from "./contracts/common";
import type { EffectiveFlags, Flag, FlagOverride, Setting, SettingWriteRequest } from "./contracts/settings";

export interface ListSettingsQuery extends PageQuery {
  readonly group?: string;
  readonly search?: string;
}

export function listSettings(client: ApiClient, query: ListSettingsQuery = {}): Promise<Paginated<Setting>> {
  return client.request({ method: "GET", path: `/admin/settings${queryString({ ...query })}` });
}


export function putSetting(client: ApiClient, settingKey: string, body: SettingWriteRequest): Promise<Setting> {
  return client.request({ method: "PUT", path: `/admin/settings/${settingKey}`, body });
}

export function listFlags(client: ApiClient, query: PageQuery = {}): Promise<Paginated<Flag>> {
  return client.request({ method: "GET", path: `/admin/flags${queryString({ ...query })}` });
}

export function putFlagState(client: ApiClient, flagKey: string, enabled: boolean): Promise<Flag> {
  return client.request({ method: "PUT", path: `/admin/flags/${flagKey}`, body: { enabled } });
}

export function putFlagOverride(
  client: ApiClient,
  flagKey: string,
  merchantId: Ulid,
  body: { readonly enabled: boolean; readonly reason?: string | null },
): Promise<FlagOverride> {
  return client.request({ method: "PUT", path: `/admin/flags/${flagKey}/overrides/${merchantId}`, body });
}

export function deleteFlagOverride(client: ApiClient, flagKey: string, merchantId: Ulid): Promise<null> {
  return client.request({ method: "DELETE", path: `/admin/flags/${flagKey}/overrides/${merchantId}` });
}

/**
 * Effective flags for the signed-in caller. Deliberately NOT the admin flag
 * catalog: gating a screen on `listFlags` would make the screen's existence
 * depend on holding `settings.flag.view`, which most operators do not.
 */
export function getEffectiveFlags(client: ApiClient): Promise<EffectiveFlags> {
  return client.request({ method: "GET", path: "/config/flags" });
}
